import { OTLayout } from "@/components/layout/ot-layout";
import { TrendingUp, ChevronDown, ChevronUp } from "lucide-react";
import { useState } from "react";
import { Link } from "wouter";

const BANDS = [
  {
    band: "Band 5",
    title: "Newly Qualified OT",
    description: "Entry-level registered OT post. Consolidating core competency, working under supervision, developing clinical reasoning across rheumatology conditions.",
    typical_roles: ["Rotational OT", "Junior rheumatology OT", "Community OT (rheumatology caseload)"],
    key_focuses: [
      "Completing preceptorship (typically 12 months)",
      "Consolidating use of core OT assessments (COPM, HAQ-DI, DASH)",
      "Learning joint protection programme delivery",
      "Developing therapeutic splinting skills under supervision",
      "Building MDT working relationships in rheumatology",
      "Engaging with RCOT and joining RCOT SS Rheumatology",
    ],
    progression_to: "Band 6 typically after 2–4 years post-qualification, when demonstrating independent practice and developing specialist knowledge.",
    colour: "blue",
  },
  {
    band: "Band 6",
    title: "Specialist OT",
    description: "Experienced OT working with increasing independence in rheumatology. Developing specialist knowledge, beginning to supervise students and junior colleagues.",
    typical_roles: ["Specialist rheumatology OT", "Senior rotational OT", "Community rheumatology specialist OT"],
    key_focuses: [
      "Independent management of complex rheumatology caseloads",
      "Delivering and evaluating joint protection and fatigue management programmes",
      "Specialist hand assessment and splinting skills",
      "Supervision of Band 5 OTs and OT students",
      "Contributing to service development and audit",
      "RCOT SS Rheumatology CPD and specialist section engagement",
    ],
    progression_to: "Band 7 after demonstrating specialist expertise, leadership skills, and contribution to service development — typically 3–5 years at Band 6.",
    colour: "amber",
  },
  {
    band: "Band 7",
    title: "Highly Specialist / Advanced OT",
    description: "Highly specialist OT with recognised expertise in rheumatology OT. Leading clinical services, supervising and developing colleagues, contributing to national practice.",
    typical_roles: ["Highly specialist rheumatology OT", "Advanced practitioner OT", "Clinical specialist OT (rheumatology)"],
    key_focuses: [
      "Clinical leadership of rheumatology OT service",
      "Advanced clinical assessment and intervention skills",
      "Managing the most complex patients including rare diseases",
      "Supporting postgraduate students and Band 5/6 staff",
      "Service evaluation, audit, and quality improvement leadership",
      "National engagement: BSR OT SIG, RCOT SS Rheumatology committee",
    ],
    progression_to: "Band 8a or Consultant OT role — requires demonstrating strategic leadership, advanced scope of practice, and national contributions.",
    colour: "orange",
  },
  {
    band: "Band 8a–8c",
    title: "Consultant / Strategic OT",
    description: "Consultant OT leading at a strategic level — influencing national practice, holding advanced clinical scope, and developing the OT workforce in rheumatology.",
    typical_roles: ["Consultant OT (rheumatology)", "Advanced OT (Band 8a)", "Head of OT / Clinical Lead OT"],
    key_focuses: [
      "Strategic clinical leadership across rheumatology OT services",
      "Extended scope of practice (e.g. prescribing authority, imaging referral)",
      "National influence: NICE guideline development, NHS England strategy",
      "RCOT Fellowship (FRCOT) preparation and application",
      "Research leadership, publication, and dissemination",
      "Cross-system leadership and workforce transformation",
    ],
    progression_to: "RCOT Fellowship (FRCOT) or national/academic leadership roles at Band 8c/9.",
    colour: "rose",
  },
];

const COLOUR_MAP: Record<string, { header: string; badge: string; bar: string }> = {
  blue: { header: "from-blue-700 to-blue-900", badge: "bg-blue-100 text-blue-800", bar: "bg-blue-400" },
  amber: { header: "from-amber-700 to-amber-900", badge: "bg-amber-100 text-amber-800", bar: "bg-amber-400" },
  orange: { header: "from-orange-700 to-orange-900", badge: "bg-orange-100 text-orange-800", bar: "bg-orange-400" },
  rose: { header: "from-rose-700 to-rose-900", badge: "bg-rose-100 text-rose-800", bar: "bg-rose-400" },
};

export default function OtBandProgression() {
  const [expanded, setExpanded] = useState<string | null>("Band 5");

  return (
    <OTLayout title="Band Progression">
      <div className="space-y-6">

        <div className="bg-gradient-to-br from-amber-700 to-orange-800 rounded-2xl p-6 text-white">
          <div className="flex items-center gap-3 mb-2">
            <TrendingUp className="h-7 w-7 text-amber-300" />
            <div>
              <h2 className="text-xl font-bold">NHS Band Progression — Rheumatology OT</h2>
              <p className="text-amber-200 text-sm">Band 5 → 6 → 7 → 8a–8c Agenda for Change framework</p>
            </div>
          </div>
          <p className="text-amber-100 text-sm leading-relaxed">
            Understand the expectations, key competencies, and development priorities at each NHS Agenda for Change band for rheumatology occupational therapists. Use this alongside your RCOT Career Development Framework and HCPC CPD portfolio to plan your progression.
          </p>
        </div>

        <div className="space-y-4">
          {BANDS.map((b) => {
            const c = COLOUR_MAP[b.colour];
            const isOpen = expanded === b.band;
            return (
              <div key={b.band} className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                <button
                  onClick={() => setExpanded(isOpen ? null : b.band)}
                  className={`w-full bg-gradient-to-r ${c.header} text-white p-5 flex items-center justify-between gap-4 text-left`}
                >
                  <div>
                    <div className="flex items-center gap-3 mb-0.5">
                      <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${c.badge}`}>{b.band}</span>
                      <h3 className="text-base font-bold">{b.title}</h3>
                    </div>
                    <p className="text-sm opacity-80">{b.description}</p>
                  </div>
                  {isOpen ? <ChevronUp className="h-5 w-5 shrink-0 opacity-70" /> : <ChevronDown className="h-5 w-5 shrink-0 opacity-70" />}
                </button>
                {isOpen && (
                  <div className="p-5 space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <p className="text-xs font-semibold text-slate-700 mb-2">Typical roles</p>
                        <ul className="space-y-1">
                          {b.typical_roles.map((r, i) => (
                            <li key={i} className="flex items-center gap-2 text-sm text-slate-600">
                              <span className={`h-1.5 w-1.5 rounded-full ${c.bar} shrink-0`} />
                              {r}
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-slate-700 mb-2">Key development focuses</p>
                        <ul className="space-y-1">
                          {b.key_focuses.slice(0, 4).map((f, i) => (
                            <li key={i} className="flex items-start gap-2 text-sm text-slate-600">
                              <span className={`mt-1.5 h-1.5 w-1.5 rounded-full ${c.bar} shrink-0`} />
                              {f}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                    {b.key_focuses.length > 4 && (
                      <div>
                        <p className="text-xs font-semibold text-slate-700 mb-2">Additional focuses</p>
                        <ul className="space-y-1">
                          {b.key_focuses.slice(4).map((f, i) => (
                            <li key={i} className="flex items-start gap-2 text-sm text-slate-600">
                              <span className={`mt-1.5 h-1.5 w-1.5 rounded-full ${c.bar} shrink-0`} />
                              {f}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    <div className="bg-slate-50 rounded-xl p-4">
                      <p className="text-xs font-semibold text-slate-700 mb-1">Progression to next band</p>
                      <p className="text-sm text-slate-600 leading-relaxed">{b.progression_to}</p>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
          <h3 className="font-bold text-slate-900 mb-3">Planning Your Progression</h3>
          <p className="text-sm text-slate-600 leading-relaxed mb-4">
            Use your CPD portfolio and RCOT Career Development Framework to gather evidence for band progression applications. Discuss your development goals with your line manager in annual appraisal and seek formal clinical supervision.
          </p>
          <div className="flex flex-wrap gap-3">
            <Link href="/ot/cpd/advanced-practice">
              <button className="px-4 py-2 bg-amber-700 hover:bg-amber-800 text-white rounded-xl text-sm font-medium transition-colors">
                Open Advanced Practice Portfolio
              </button>
            </Link>
            <Link href="/ot/cpd">
              <button className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-sm font-medium transition-colors">
                CPD Activity Log
              </button>
            </Link>
          </div>
        </div>

        <div className="text-xs text-slate-400 bg-slate-50 rounded-xl px-4 py-3 leading-relaxed">
          <strong>NHS Agenda for Change:</strong> Band boundaries and role expectations vary between NHS trusts and regions. Use this as a general guide — always refer to the Agenda for Change job evaluation handbook and your local job profiles when applying for posts or making a banding appeal.
        </div>
      </div>
    </OTLayout>
  );
}
